import React, { useEffect, useState, useRef } from 'react';
import { Lock, Loader2, AlertCircle, AlertTriangle, ShieldCheck, Activity, Shield, MessageSquare, TrendingDown, Zap, Clock, Eye, Sparkles, CheckCircle2, Star, Radio, ChevronRight, Info, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { createPayment, redirectToPayment } from '../../services/paymentService';
import { getPatternContext, getValidationMessage, getEmotionalSupportCopy, PatternContext } from '../../utils/patternContext';
interface TeaserResultCardProps {
  scanId: string;
  messageCount: number;
  scanTitle: string;
  score?: number;
  riskLevel?: 'low' | 'medium' | 'high' | null;
  patterns?: any[];
  onUnlockSuccess?: () => void;
}
export function TeaserResultCard({
  scanId,
  messageCount,
  scanTitle,
  score = 0,
  riskLevel: rawRiskLevel,
  patterns = [],
  onUnlockSuccess
}: TeaserResultCardProps) {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [isLive, setIsLive] = useState(true);
  const [liveScansCount, setLiveScansCount] = useState(0);
  const [expandedPattern, setExpandedPattern] = useState<number | null>(null);
  const scoreAnimationRef = useRef<NodeJS.Timeout | null>(null);
  // Use ref for onUnlockSuccess to prevent real-time channel recreation
  const onUnlockSuccessRef = useRef(onUnlockSuccess);
  useEffect(() => {
    onUnlockSuccessRef.current = onUnlockSuccess;
  }, [onUnlockSuccess]);
  // Derive risk level from score if not provided
  const riskLevel = rawRiskLevel || (score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low');
  // Risk level configuration
  const riskConfig = {
    high: {
      color: '#ef4444',
      colorRgb: '239, 68, 68',
      bgClass: 'bg-red-500/10',
      textClass: 'text-red-400',
      borderClass: 'border-red-500/20',
      label: 'High Risk',
      ringGlow: 'rgba(239, 68, 68, 0.25)',
      pulseClass: 'animate-pulse'
    },
    medium: {
      color: '#f59e0b',
      colorRgb: '245, 158, 11',
      bgClass: 'bg-amber-500/10',
      textClass: 'text-amber-400',
      borderClass: 'border-amber-500/20',
      label: 'Medium Risk',
      ringGlow: 'rgba(245, 158, 11, 0.25)',
      pulseClass: ''
    },
    low: {
      color: '#10b981',
      colorRgb: '16, 185, 129',
      bgClass: 'bg-emerald-500/10',
      textClass: 'text-emerald-400',
      borderClass: 'border-emerald-500/20',
      label: 'Low Risk',
      ringGlow: 'rgba(16, 185, 129, 0.25)',
      pulseClass: ''
    }
  };
  const config = riskConfig[riskLevel];
  // Animated score counter
  useEffect(() => {
    if (scoreAnimationRef.current) {
      clearInterval(scoreAnimationRef.current);
    }
    const duration = 1500;
    const steps = 60;
    const increment = score / steps;
    let current = 0;
    scoreAnimationRef.current = setInterval(() => {
      current += increment;
      if (current >= score) {
        setAnimatedScore(score);
        if (scoreAnimationRef.current) clearInterval(scoreAnimationRef.current);
      } else {
        setAnimatedScore(Math.floor(current));
      }
    }, duration / steps);
    return () => {
      if (scoreAnimationRef.current) clearInterval(scoreAnimationRef.current);
    };
  }, [score]);
  // Fetch live scans count for social proof.
  // Only show the count if Supabase returns a real number — never fall back to Math.random.
  useEffect(() => {
    const fetchLiveCount = async () => {
      try {
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        const {
          count
        } = await supabase.from('scans').select('*', {
          count: 'exact',
          head: true
        }).gte('created_at', fiveMinutesAgo);
        if (typeof count === 'number') {
          setLiveScansCount(count);
        }
      } catch {
        // Stay silent — never invent a number.
      }
    };
    fetchLiveCount();
    const interval = setInterval(fetchLiveCount, 30000);
    return () => clearInterval(interval);
  }, []);
  // Real-time subscription for unlock status
  useEffect(() => {
    if (!scanId) return;
    const channel = supabase.channel(`teaser-${scanId}`).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'scans',
      filter: `id=eq.${scanId}`
    }, payload => {
      console.log('[TeaserResultCard] Real-time update received:', payload.new);
      if (payload.new.is_unlocked === true) {
        console.log('[TeaserResultCard] Scan unlocked via real-time!');
        onUnlockSuccessRef.current?.();
      }
    }).subscribe(status => {
      console.log('[TeaserResultCard] Subscription status:', status);
      setIsLive(status === 'SUBSCRIBED');
    });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [scanId]);
  // SVG ring calculations
  const radius = 70;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - animatedScore / 100 * circumference;
  const ringSize = 180;
  // Pattern helpers
  const getPatternInfo = (pattern: any) => {
    if (typeof pattern === 'string') {
      return {
        type: pattern,
        severity: 'MEDIUM'
      };
    }
    return {
      type: pattern.type || 'Unknown',
      severity: pattern.severity || 'MEDIUM'
    };
  };
  const getSeverityColor = (severity: string) => {
    const s = severity?.toUpperCase();
    if (s === 'HIGH') return 'bg-red-500/15 text-red-400 border-red-500/30';
    if (s === 'MEDIUM') return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
    return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
  };
  const getPatternIcon = (type: string) => {
    if (!type) return Clock;
    const lower = type.toLowerCase();
    if (lower.includes('gaslight')) return AlertTriangle;
    if (lower.includes('manipulat')) return Shield;
    if (lower.includes('dismiss')) return MessageSquare;
    if (lower.includes('blame')) return TrendingDown;
    if (lower.includes('aggress') || lower.includes('abuse')) return Zap;
    if (lower.includes('control')) return Lock;
    if (lower.includes('silent') || lower.includes('stonewall')) return Radio;
    return AlertCircle;
  };
  const handleUnlock = async () => {
    if (!user) {
      setError('Please sign in to unlock results');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log('[TeaserResultCard] Initiating payment for scan:', scanId);
      // Use centralized payment service with proper auth and scan_id
      const {
        authorization_url
      } = await createPayment('single_scan', {
        scanId
      });
      console.log('[TeaserResultCard] Redirecting to payment:', authorization_url);
      // Redirect to Paystack payment page
      redirectToPayment(authorization_url);
    } catch (err: any) {
      console.error('[TeaserResultCard] Payment error:', err);
      setError(err.message || 'Failed to initialize payment. Please try again.');
      setLoading(false);
    }
  };
  // Get validation and support messaging
  const patternTypes = patterns.map(p => typeof p === 'string' ? p : p.type || '');
  const validationMessage = getValidationMessage(riskLevel, patterns.length);
  const supportCopy = getEmotionalSupportCopy(patternTypes);
  return <div className="w-full max-w-lg mx-auto px-4 py-6" style={{
    '--theme-color': config.color
  } as React.CSSProperties} data-id="element-380" data-mp="22msevqojhs1d">
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative" data-id="element-381" data-mp="8ac0u3pi59ly">
        {/* Main Card */}
        <div className="bg-[#0a0a0a] rounded-[28px] border border-white/[0.08] shadow-2xl overflow-hidden relative" data-id="element-382" data-mp="3q9elha81tzxv">
          {/* Animated Background Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none opacity-40" style={{
          backgroundColor: config.ringGlow
        }} data-id="element-383" data-mp="32kdiw3lwdj0i" />

          {/* Live Indicator Bar */}
          <div className="relative z-10 px-6 pt-5 pb-4 border-b border-white/[0.06]" data-id="element-384" data-mp="36k4b8br2o775">
            <div className="flex items-center justify-between" data-id="element-385" data-mp="qsq5264xgpv3">
              <div className="flex items-center gap-2" data-id="element-386" data-mp="lvzq5m4v00b9">
                <span className="relative flex h-2.5 w-2.5" data-id="element-387" data-mp="lxkkban9hh8l">
                  <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${isLive ? 'bg-emerald-400 animate-ping' : 'bg-gray-500'}`} data-id="element-388" data-mp="ubfi2y0opoz2" />
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isLive ? 'bg-emerald-500' : 'bg-gray-600'}`} data-id="element-389" data-mp="kf2mtvphr327" />
                </span>
                <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider" data-id="element-390" data-mp="1d3ri3e3v8e61">
                  {isLive ? 'Live Analysis' : 'Connecting...'}
                </span>
              </div>
              {liveScansCount > 0 && <div className="flex items-center gap-1.5 text-[11px] text-white/40" data-id="element-391" data-mp="13aspzm95u5ho">
                  <Activity size={12} className="text-white/30" data-id="element-392" data-mp="18a5h4j72dyq2" />
                  <span data-id="element-393" data-mp="1w0j969pnfxkn">{liveScansCount} scans in progress</span>
                </div>}
            </div>
          </div>

          <div className="relative z-10 p-6 md:p-8" data-id="element-394" data-mp="20tlwi03u2ase">
            {/* Score Ring Section */}
            <div className="flex flex-col items-center mb-8" data-id="element-395" data-mp="3ga725eiywdzz">
              <div className="relative mb-5" style={{
              width: ringSize,
              height: ringSize
            }} data-id="element-396" data-mp="3l8qr7nsuy5wk">
                {/* Outer glow ring */}
                <div className="absolute inset-0 rounded-full blur-[60px] opacity-20 pointer-events-none" style={{
                backgroundColor: config.color
              }} data-id="element-397" data-mp="3c5zbhbsy8nz4" />

                <svg className="w-full h-full transform -rotate-90 relative z-10" style={{
                filter: 'drop-shadow(0 0 12px var(--theme-color))'
              }} data-id="element-398" data-mp="2oihqbz46wo54">
                  {/* Background ring */}
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} fill="transparent" data-id="element-399" data-mp="2emefm4v9vh6k" />
                  {/* Progress ring */}
                  <motion.circle initial={{
                  strokeDashoffset: circumference
                }} animate={{
                  strokeDashoffset: offset
                }} transition={{
                  duration: 1.5,
                  ease: [0.22, 1, 0.36, 1]
                }} cx={ringSize / 2} cy={ringSize / 2} r={radius} stroke={config.color} strokeWidth={strokeWidth} fill="transparent" strokeDasharray={circumference} strokeLinecap="round" data-id="element-400" data-mp="29w2bd5c7rpx1" />
                </svg>

                {/* Score Display */}
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-401" data-mp="34ototbcswtv1">
                  <motion.span className="text-6xl font-black text-white tracking-tighter tabular-nums" initial={{
                  opacity: 0,
                  scale: 0.5
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.5,
                  delay: 0.3
                }} data-id="element-402" data-mp="39n4vxebpoq9k">
                    {animatedScore}
                  </motion.span>
                  <span className="text-[11px] font-mono font-bold text-white/40 uppercase tracking-widest mt-1" data-id="element-403" data-mp="15qix4bi9ls0">
                    Toxicity Score
                  </span>
                </div>
              </div>

              {/* Risk Badge */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border ${config.bgClass} ${config.borderClass}`} data-id="element-404" data-mp="27z2lcmmj7wd3">
                <span className="w-2 h-2 rounded-full" style={{
                backgroundColor: 'var(--theme-color)',
                boxShadow: '0 0 8px var(--theme-color)'
              }} data-id="element-405" data-mp="181sg5gs3ys8c" />
                <span className={`text-sm font-mono font-bold uppercase tracking-wider ${config.textClass}`} data-id="element-406" data-mp="yzj6et4nz2w0">
                  {config.label}
                </span>
              </motion.div>
            </div>

            {/* Validation Message */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="text-center mb-6" data-id="element-407" data-mp="3er762wb2b9ii">
              <p className="text-white/70 text-sm leading-relaxed max-w-sm mx-auto" data-id="element-408" data-mp="xjwzxanm0wy8">
                {validationMessage}
              </p>
              {supportCopy && <p className="text-white/50 text-xs mt-2 flex items-center justify-center gap-1.5" data-id="element-409" data-mp="1q7sykkwqoyxn">
                  <Heart size={12} className="text-red-400" data-id="element-410" data-mp="1u9mtsdo61aqf" />
                  {supportCopy}
                </p>}
            </motion.div>

            {/* Analysis Stats */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-white/[0.03] rounded-2xl p-5 mb-6 border border-white/[0.06]" data-id="element-411" data-mp="1l8gci2dozvw4">
              <div className="flex items-center justify-center gap-8" data-id="element-412" data-mp="2bldynzbtvlj">
                <div className="text-center" data-id="element-413" data-mp="3tfs5nmno5557">
                  <div className="text-3xl font-bold text-white font-mono" data-id="element-414" data-mp="ba6ukdl8n0jo">
                    {messageCount}
                  </div>
                  <div className="text-[10px] font-mono font-medium text-white/40 uppercase tracking-wider mt-1" data-id="element-415" data-mp="6cnct3pc05mz">
                    Messages
                  </div>
                </div>
                <div className="w-px h-12 bg-white/10" data-id="element-416" data-mp="3aqlk7rvhpzfp" />
                <div className="text-center" data-id="element-417" data-mp="3ogyq7ootn42e">
                  <div className="text-3xl font-bold text-white font-mono" data-id="element-418" data-mp="3oi0belzjx6xu">
                    {patterns.length}
                  </div>
                  <div className="text-[10px] font-mono font-medium text-white/40 uppercase tracking-wider mt-1" data-id="element-419" data-mp="3jo5ozaskadv2">
                    Patterns
                  </div>
                </div>
                <div className="w-px h-12 bg-white/10" data-id="element-420" data-mp="2r1b5oh7hdkj6" />
                <div className="text-center" data-id="element-421" data-mp="uz7ztzz0j954">
                  <div className={`text-3xl font-bold font-mono ${config.textClass}`} data-id="element-422" data-mp="uwc4mjejuykd">
                    {riskLevel.toUpperCase()}
                  </div>
                  <div className="text-[10px] font-mono font-medium text-white/40 uppercase tracking-wider mt-1" data-id="element-423" data-mp="14qcgd44c7222">
                    Risk
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Pattern Cards with Rich Context */}
            {patterns.length > 0 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="mb-6" data-id="element-424" data-mp="lxrqlvjxpd2h">
                <div className="flex items-center justify-between mb-3 px-1" data-id="element-425" data-mp="21eihvi5ypec4">
                  <h4 className="text-xs font-mono font-bold text-white/50 uppercase tracking-wider flex items-center gap-2" data-id="element-426" data-mp="21glbsda8jrm4">
                    <Sparkles size={12} className={config.textClass} data-id="element-427" data-mp="3hv7r9ya7z67u" />
                    Patterns Detected
                  </h4>
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-white/30 uppercase tracking-wider" data-id="element-428" data-mp="3d37lhp7gxj3c">
                    <Lock size={10} data-id="element-429" data-mp="384wahb5ft2jj" />
                    <span data-id="element-430" data-mp="335tfykn32ept">Details locked</span>
                  </div>
                </div>

                <div className="space-y-2" data-id="element-431" data-mp="2y6ymmorhp7rg">
                  {patterns.slice(0, 3).map((p: any, i: number) => {
                const info = getPatternInfo(p);
                const PatternIcon = getPatternIcon(info.type);
                const context = getPatternContext(info.type);
                const isExpanded = expandedPattern === i;
                return <motion.div key={i} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.7 + i * 0.1
                }} className="group" data-id="element-432" data-mp="sbnuondh1fip" data-mpi={i}>
                        <button onClick={() => setExpandedPattern(isExpanded ? null : i)} className="w-full bg-white/[0.04] hover:bg-white/[0.06] border border-white/[0.08] rounded-xl p-4 transition-all duration-200" data-id="element-433" data-mp="39opqebuqppdr">
                          <div className="flex items-center justify-between" data-id="element-434" data-mp="34pwn1c963erb">
                            <div className="flex items-center gap-3" data-id="element-435" data-mp="xzyiaovv0p7c">
                              <div className={`p-2 rounded-lg ${config.bgClass}`} data-id="element-436" data-mp="12xrm5b9ox74g">
                                <PatternIcon size={16} className={config.textClass} data-id="element-437" data-mp="2ngkaipztslq7" />
                              </div>
                              <div className="text-left" data-id="element-438" data-mp="2x7yizn9r8vvh">
                                <span className="text-white font-semibold text-sm capitalize block" data-id="element-439" data-mp="2se3zaju5ggwe">
                                  {info.type}
                                </span>
                                {context && <span className="text-white/40 text-xs line-clamp-1" data-id="element-440" data-mp="31776wbi45zj4">
                                    {context.definition.slice(0, 50)}...
                                  </span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-441" data-mp="1lst13c11yqra">
                              <span className={`px-2.5 py-1 rounded-full text-[9px] font-mono font-bold uppercase tracking-wider border ${getSeverityColor(info.severity)}`} data-id="element-442" data-mp="1gsx6g3e8wboe">
                                {info.severity}
                              </span>
                              <ChevronRight size={14} className={`text-white/30 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-443" data-mp="3jlz3ojtebmfu" />
                            </div>
                          </div>
                        </button>

                        {/* Expanded Context Preview */}
                        <AnimatePresence data-id="element-444" data-mp="30xk7li481i86">
                          {isExpanded && context && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-445" data-mp="2vvv0gdk4dopd">
                              <div className="pt-2 pb-1 px-4" data-id="element-446" data-mp="16fr9xoll4ylt">
                                <div className="bg-white/[0.02] rounded-lg p-4 border border-white/[0.05] relative overflow-hidden" data-id="element-447" data-mp="16i52nqsslyq7">
                                  {/* Blur overlay for locked content */}
                                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a] z-10 flex items-end justify-center pb-4" data-id="element-448" data-mp="wnuzde2bbg0p">
                                    <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider flex items-center gap-1.5" data-id="element-449" data-mp="1p839k3kutls9">
                                      <Lock size={10} data-id="element-450" data-mp="1v16t4ir7gbnn" />
                                      Unlock for full context
                                    </span>
                                  </div>

                                  <div className="space-y-3 blur-[2px]" data-id="element-451" data-mp="1l2zql4xnxw56">
                                    <div data-id="element-452" data-mp="1l4u07n3hi1ty">
                                      <div className="text-[10px] font-bold text-white/30 uppercase tracking-wider mb-1" data-id="element-453" data-mp="1g3wkdh5y5jvm">
                                        Why This Matters
                                      </div>
                                      <p className="text-white/60 text-xs leading-relaxed" data-id="element-454" data-mp="17c4frn3lt4yy">
                                        {context.whyItMatters}
                                      </p>
                                    </div>
                                    <div data-id="element-455" data-mp="xezqheegotdx">
                                      <div className="text-[10px] font-bold text-white/30 uppercase tracking-wider mb-1" data-id="element-456" data-mp="sdshjc0l1o5o">
                                        How It Feels
                                      </div>
                                      <p className="text-white/60 text-xs leading-relaxed" data-id="element-457" data-mp="n7x1wi2cuqqb">
                                        {context.emotionalImpact}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </motion.div>;
              })}

                  {/* Additional patterns indicator */}
                  {patterns.length > 3 && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 1
              }} className="flex items-center justify-center py-2" data-id="element-458" data-mp="ead2zfryt6iv">
                      <span className="text-xs font-medium text-white/30 bg-white/[0.03] px-3 py-1.5 rounded-full border border-white/[0.06]" data-id="element-459" data-mp="1x77ysmey4spr">
                        +{patterns.length - 3} more patterns locked
                      </span>
                    </motion.div>}
                </div>
              </motion.div>}

            {/* Risk-Aware CTA Banner */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.75
          }} className={`mb-6 p-5 rounded-2xl border relative overflow-hidden ${config.bgClass} ${config.borderClass}`} data-id="element-460" data-mp="2g62d5uahzr5e">
              <div className={`absolute inset-0 opacity-20 ${config.pulseClass}`} style={{
              backgroundColor: config.color
            }} data-id="element-461" data-mp="2sfs0r9unffhv" />
              <div className="relative z-10 flex items-start gap-4" data-id="element-462" data-mp="2ir1on55wab55">
                <div className={`p-2 rounded-xl bg-black/20 shrink-0 ${config.textClass}`} data-id="element-463" data-mp="2nplevhriu93b">
                  <Eye size={24} data-id="element-464" data-mp="29pak43n3cacv" />
                </div>
                <div data-id="element-465" data-mp="29rwueb1d93ys">
                  <h4 className="text-white font-bold text-base mb-1 leading-tight" data-id="element-466" data-mp="2eon8istm1phk">
                    You scored {score}/100 — {config.label}.
                    <br data-id="element-467" data-mp="1zsxrl93a82jp" />
                    <span className={config.textClass} data-id="element-468" data-mp="1ztquu03189x3">
                      {riskLevel === 'high' ? 'Unlock the full report to see exactly where the manipulation is happening.' : riskLevel === 'medium' ? 'Unlock the full report to understand the subtle patterns we detected.' : 'Unlock the full report to confirm your communication is healthy.'}
                    </span>
                  </h4>
                  <p className="text-white/60 text-xs mt-2 leading-relaxed" data-id="element-469" data-mp="3sqkyt2iitkgb">
                    See specific examples, expert recommendations, and a
                    shareable PDF report.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Error Message */}
            <AnimatePresence data-id="element-470" data-mp="kaj48sgbyxmh">
              {error && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="mb-4" data-id="element-471" data-mp="fawq8gsohdcm">
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3" data-id="element-472" data-mp="y6d3skv7samx">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-473" data-mp="2gxkcilx42x8n" />
                    <p className="text-sm text-red-200" data-id="element-474" data-mp="2lweujln98hrp">{error}</p>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* CTA Section */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8
          }} className="space-y-4" data-id="element-475" data-mp="2usw4iljvu9lv">
              {/* What You'll Get */}
              <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-476" data-mp="3dk6v11lp4kd8">
                <div className="flex items-center gap-2 text-xs text-white/50" data-id="element-477" data-mp="3ikthwh2zd9p3">
                  <CheckCircle2 size={14} className="text-emerald-400" data-id="element-478" data-mp="3iiqk3avl6c9t" />
                  <span data-id="element-479" data-mp="3njxpx4e651du">Full pattern analysis</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/50" data-id="element-480" data-mp="2yffyki9bj24d">
                  <CheckCircle2 size={14} className="text-emerald-400" data-id="element-481" data-mp="2thxnombqqctq" />
                  <span data-id="element-482" data-mp="37h6zzawtsmsf">Specific examples</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/50" data-id="element-483" data-mp="37ge1sybxn5lw">
                  <CheckCircle2 size={14} className="text-emerald-400" data-id="element-484" data-mp="33gwudzcy3ye3" />
                  <span data-id="element-485" data-mp="2fphm7pyhmmux">Expert recommendations</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/50" data-id="element-486" data-mp="2aqwp3zej8jap">
                  <CheckCircle2 size={14} className="text-emerald-400" data-id="element-487" data-mp="2aotpqorhpdtb" />
                  <span data-id="element-488" data-mp="2oj4f98bb9c0f">Shareable PDF report</span>
                </div>
              </div>

              {/* Primary CTA */}
              <motion.button onClick={handleUnlock} disabled={loading} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full bg-white text-black font-bold py-4 px-6 rounded-2xl transition-all shadow-[0_0_30px_rgba(255,255,255,0.15)] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-base" data-id="element-489" data-mp="2jpi3dicz1c5o">
                {loading ? <>
                    <Loader2 className="w-5 h-5 animate-spin" data-id="element-490" data-mp="1cy6psarbaqix" />
                    Processing...
                  </> : <>
                    <Lock size={18} data-id="element-491" data-mp="25mm5z0m6jevu" />
                    Unlock Full Report — $2.99
                  </>}
              </motion.button>

              {/* Secondary CTA */}
              <button onClick={() => navigate('/payment')} className="w-full text-[11px] text-white/40 hover:text-white/70 font-semibold uppercase tracking-[0.15em] transition-colors py-2" data-id="element-492" data-mp="1vnmtf6yamz3k">
                or upgrade to unlimited scans →
              </button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1
          }} className="mt-6 pt-5 border-t border-white/[0.06]" data-id="element-493" data-mp="3dzy11062l8kl">
              <div className="flex items-center justify-center gap-6" data-id="element-494" data-mp="3rva35smk21bj">
                <div className="flex items-center gap-1.5" data-id="element-495" data-mp="2ubp9msccp6m5">
                  <ShieldCheck size={14} className="text-white/30" data-id="element-496" data-mp="2z87bnkgaqqus" />
                  <span className="text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-497" data-mp="34822jo02ngfu">
                    Private
                  </span>
                </div>
                <div className="w-px h-3 bg-white/10" data-id="element-498" data-mp="3903ch39rov2p" />
                <div className="flex items-center gap-1.5" data-id="element-499" data-mp="2bk668keezqjl">
                  <Zap size={14} className="text-white/30" data-id="element-500" data-mp="2bhjx4gbvl2z9" />
                  <span className="text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-501" data-mp="2ggwlx6mb4k4h">
                    Instant
                  </span>
                </div>
                <div className="w-px h-3 bg-white/10" data-id="element-502" data-mp="23zlv0ww0wu62" />
                <div className="flex items-center gap-1" data-id="element-503" data-mp="23y0zp51vx775">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} className="text-amber-400" fill="currentColor" data-id="element-504" data-mp="1u1plg9iqi7a2" />)}
                  <span className="text-[10px] font-medium text-white/30 ml-1" data-id="element-505" data-mp="1l8uar0ahzjcw">
                    4.9
                  </span>
                </div>
              </div>
              <p className="text-center text-[10px] text-white/20 mt-3" data-id="element-506" data-mp="1g7y80q1vu392">
                Encrypted · Anonymous · Never trained on
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>;
}